import { useMutation } from '@tanstack/react-query';

import { useTrackInvitedLibraryMember } from '@air/analytics';
import { Libraries } from '@air/api';
import { type Library } from '@air/api/types';

import { getLibraryMembersKey } from '~/components/LibraryBeta/hooks/useLibraryMembers';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { queryClient } from '~/swr-hooks/queryClient';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';

type TargetLibrary = {
  libraryId: Library['id'];
  roleId: string;
};

export type AddMemberToLibrariesInput = {
  email: string;
  libraries: TargetLibrary[];
};

export type AddMemberToLibrariesResult = {
  succeeded: TargetLibrary[];
  total: number;
};

export const useAddMemberToLibraries = () => {
  const { trackInvitedLibraryMember } = useTrackInvitedLibraryMember();
  const { currentWorkspace } = useCurrentWorkspace();

  const addMemberToLibraries = useMutation({
    mutationFn: async ({ email, libraries }: AddMemberToLibrariesInput): Promise<AddMemberToLibrariesResult> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspaceId');
      }

      const results = await Promise.allSettled(
        libraries.map(({ libraryId, roleId }) =>
          Libraries.addMembers({ workspaceId, libraryId, members: [{ email, roleId }] }).then(() => ({
            libraryId,
            roleId,
          })),
        ),
      );

      const succeeded = results
        .filter((r): r is PromiseFulfilledResult<TargetLibrary> => r.status === 'fulfilled')
        .map((r) => r.value);

      if (succeeded.length === 0) {
        throw new Error('Failed to add member to any libraries');
      }

      return { succeeded, total: libraries.length };
    },

    onError: (error) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to add member to libraries',
      });
    },

    onSuccess: ({ succeeded }, { email }) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      succeeded.forEach(({ libraryId, roleId }) => {
        trackInvitedLibraryMember({
          library_id: libraryId,
          library_member_email: email,
          library_member_role_id: roleId,
        });
        queryClient.invalidateQueries({ queryKey: getLibraryMembersKey(libraryId) });
      });
    },
  });

  return { addMemberToLibraries };
};
